import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionTemplate, useMotionValue } from "framer-motion";
import { ArrowRight, Car, Check, FileCheck2, Home as HomeIcon, ScanLine, ShieldCheck } from "lucide-react";
import { Reveal } from "../site/Reveal";
import { FaqList } from "../site/FaqList";
import { FAQ } from "../../data/faq";
import { api } from "../../lib/api";

export function AdasFeature() {
  return (
    <section className="bg-ag-navy px-5 py-24 text-white lg:px-8" data-testid="home-adas-section">
      <div className="mx-auto grid max-w-[1320px] items-center gap-14 lg:grid-cols-12">
        <Reveal className="lg:col-span-6">
          <div className="windshield-frame aspect-[4/3] overflow-hidden bg-black">
            <img src="/images/camera-adas.webp" alt="Module de caméra ADAS fixé derrière le pare-brise et le rétroviseur intérieur, calibration à Haguenau" loading="lazy" className="h-full w-full object-cover" width="1264" height="848" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6">
          <p className="eyebrow text-ag-yellow">Calibration · Recalibrage caméra</p>
          <h2 className="mt-3 font-display text-4xl font-extrabold leading-[1] tracking-tight sm:text-5xl">Calibration caméra et ADAS à Haguenau</h2>
          <p className="mt-6 text-blue-100">Certains véhicules disposent de caméras et de systèmes d'aide à la conduite (ADAS) pouvant nécessiter une calibration après certaines interventions sur le pare-brise.</p>
          <ul className="mt-7 space-y-3">
            {["Outils professionnels de calibration", "Procédure adaptée aux préconisations du véhicule", "Vérification de votre véhicule à partir de l'immatriculation"].map((t) => (
              <li key={t} className="flex items-start gap-3"><ScanLine className="mt-0.5 h-5 w-5 shrink-0 text-ag-yellow" />{t}</li>
            ))}
          </ul>
          <Link to="/calibration-camera-adas-haguenau" className="btn btn-white mt-9" data-testid="home-adas-link">Tout savoir sur la calibration ADAS <ArrowRight className="h-4 w-4" /></Link>
        </Reveal>
      </div>
    </section>
  );
}

export function OptiquesSpotlight() {
  const x = useMotionValue(60);
  const y = useMotionValue(45);
  const mask = useMotionTemplate`radial-gradient(circle at ${x}% ${y}%, rgba(0,0,0,0) 0, rgba(5,11,32,0.15) 18%, rgba(5,11,32,0.92) 48%)`;
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    x.set(((e.clientX - r.left) / r.width) * 100);
    y.set(((e.clientY - r.top) / r.height) * 100);
  };
  return (
    <section className="relative overflow-hidden bg-black px-5 py-24 text-white lg:px-8" onMouseMove={onMove} data-testid="home-optiques-section">
      <img src="/images/optique-2.webp" alt="Phare de voiture éclairé, rénovation d'optiques et de phares à Haguenau" loading="lazy" className="absolute inset-0 h-full w-full object-cover object-left" width="1400" height="933" />
      <motion.div className="absolute inset-0" style={{ background: mask }} />
      <div className="relative mx-auto grid max-w-[1320px] lg:grid-cols-12">
        <Reveal className="lg:col-span-6 lg:col-start-7">
          <p className="eyebrow text-ag-yellow">Phares · Optiques · Éclairage</p>
          <h2 className="mt-3 font-display text-4xl font-extrabold leading-[1] tracking-tight sm:text-6xl">Rénovation d'optiques à Haguenau</h2>
          <p className="mt-6 max-w-lg text-slate-200">Vos phares sont devenus moins transparents ? La rénovation d'optiques leur redonne de la clarté, pour l'esthétique comme pour la diffusion de la lumière.</p>
          <div className="mt-7 flex flex-wrap gap-2">
            {["Ternis", "Jaunis", "Oxydés", "Moins transparents"].map((t) => <span key={t} className="rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold ring-1 ring-white/20 backdrop-blur">{t}</span>)}
          </div>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/devis?service=R%C3%A9novation%20optiques" className="btn btn-yellow" data-testid="home-optiques-cta">Rénover mes optiques</Link>
            <Link to="/renovation-optiques-phares-haguenau" className="btn btn-outline-light" data-testid="home-optiques-link">En savoir plus</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function DomicilePret() {
  return (
    <section className="px-5 py-24 lg:px-8" data-testid="home-domicile-pret-section">
      <div className="mx-auto grid max-w-[1320px] gap-4 lg:grid-cols-2">
        <Reveal className="flex flex-col rounded-[2rem] bg-white p-8 ring-1 ring-slate-200 sm:p-12">
          <HomeIcon className="h-9 w-9 text-ag-blue" />
          <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-ag-ink sm:text-4xl">Remplacement de pare-brise à domicile</h2>
          <p className="mt-4 flex-1 text-slate-600">Selon votre adresse et le type d'intervention, nous pouvons intervenir à domicile ou sur votre lieu de travail. Nous vérifions d'abord que votre secteur est réellement desservi.</p>
          <Link to="/pare-brise-domicile-haguenau#verifier-adresse" className="btn btn-blue mt-8 self-start" data-testid="home-domicile-cta">Vérifier si mon adresse est desservie</Link>
        </Reveal>
        <Reveal delay={0.1} className="relative flex flex-col overflow-hidden rounded-[2rem] bg-ag-yellow p-8 text-ag-ink sm:p-12">
          <Car className="h-9 w-9" />
          <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Prêt de véhicule pendant l'intervention</h2>
          <p className="mt-4 flex-1 text-ag-ink/80">Un véhicule de prêt peut être proposé pendant votre intervention, selon disponibilité et conditions.</p>
          <Link to="/devis?mode=rendez-vous" className="btn btn-navy mt-8 self-start" data-testid="home-pret-cta">Prendre rendez-vous</Link>
        </Reveal>
      </div>
    </section>
  );
}

export function AssuranceBlock() {
  return (
    <section className="px-5 pb-24 lg:px-8" data-testid="home-assurance-section">
      <div className="mx-auto grid max-w-[1320px] items-center gap-10 rounded-[2rem] bg-ag-mist p-8 ring-1 ring-ag-blue/10 sm:p-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-6">
          <ShieldCheck className="h-10 w-10 text-ag-blue" />
          <h2 className="mt-5 font-display text-4xl font-extrabold leading-[1] tracking-tight text-ag-ink sm:text-5xl">Agréé par les assurances</h2>
          <p className="mt-5 text-lg text-slate-700">Nous vous accompagnons dans vos démarches pour votre remplacement de vitrage.</p>
          <Link to="/assurance-pare-brise-haguenau" className="btn btn-navy mt-8" data-testid="home-assurance-link">Comment ça marche ?</Link>
        </Reveal>
        <Reveal delay={0.1} className="grid gap-3 lg:col-span-6">
          {[["Vérification de votre garantie bris de glace", FileCheck2], ["Aide à la constitution de votre dossier", Check], ["Accompagnement par l'équipe du centre de Haguenau", ShieldCheck]].map(([t, I]) => (
            <div key={t} className="flex items-center gap-4 rounded-2xl bg-white p-5 ring-1 ring-slate-200"><I className="h-6 w-6 shrink-0 text-ag-blue" /><span className="font-semibold text-ag-ink">{t}</span></div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

export function CentreSection() {
  return (
    <section className="px-5 py-24 lg:px-8" data-testid="home-centre-section">
      <div className="mx-auto grid max-w-[1320px] gap-4 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <div className="relative h-full min-h-[340px] overflow-hidden rounded-[2rem] bg-ag-ink">
            <img src="/images/facade-haguenau.webp" alt="Façade du centre A+GLASS Pare-Brise HAGUENAU au 101A Route de Marienthal" loading="lazy" className="absolute inset-0 h-full w-full object-cover" width="1600" height="1213" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ag-ink/85 to-transparent p-7 pt-24 text-white sm:p-10">
              <p className="eyebrow text-ag-yellow">Notre centre</p>
              <h2 className="mt-2 font-display text-3xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl">Venez nous voir au 101A Route de Marienthal</h2>
            </div>
          </div>
        </Reveal>
        <div className="grid gap-4 lg:col-span-5">
          <Reveal delay={0.08} className="overflow-hidden rounded-[2rem] bg-ag-ink">
            <img src="/images/accueil-haguenau.webp" alt="Accueil et espace d'attente du centre A+ Glass Haguenau" loading="lazy" className="aspect-[4/3] h-full w-full object-cover" width="1360" height="1020" />
          </Reveal>
          <Reveal delay={0.14} className="rounded-[2rem] bg-white p-7 ring-1 ring-slate-200">
            <p className="text-slate-700">Un espace d'accueil pour vous recevoir pendant votre intervention, à Haguenau.</p>
            <Link to="/a-propos" className="btn btn-navy mt-5" data-testid="home-centre-link">Découvrir le centre <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function FaqPreview() {
  return (
    <section className="px-5 py-24 lg:px-8" data-testid="home-faq-section">
      <div className="mx-auto grid max-w-[1320px] gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <p className="eyebrow">Questions fréquentes</p>
          <h2 className="mt-3 font-display text-4xl font-extrabold leading-[1] tracking-tight text-ag-ink sm:text-5xl">Vos questions, nos réponses.</h2>
          <Link to="/faq" className="btn btn-outline mt-8" data-testid="home-faq-link">Toute la FAQ <ArrowRight className="h-4 w-4" /></Link>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-8"><FaqList items={FAQ.slice(0, 6)} prefix="home-faq" /></Reveal>
      </div>
    </section>
  );
}

export function BlogPreview() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/blog").then((r) => { if (alive) setPosts(Array.isArray(r.data) ? r.data.slice(0, 3) : []); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!posts.length) return null;
  return (
    <section className="px-5 pb-8 lg:px-8" data-testid="home-blog-section">
      <div className="mx-auto max-w-[1320px]">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <h2 className="font-display text-4xl font-extrabold tracking-tight text-ag-ink sm:text-5xl">Conseils & actualités</h2>
          <Link to="/blog" className="btn btn-outline" data-testid="home-blog-link">Tous les articles</Link>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {posts.map((p, i) => <PostCard key={p.slug} p={p} i={i} />)}
        </div>
      </div>
    </section>
  );
}

export function PostCard({ p, i = 0 }) {
  return (
    <Reveal delay={i * 0.06}>
      <Link to={`/blog/${p.slug}`} className="group block h-full overflow-hidden rounded-[1.5rem] bg-white ring-1 ring-slate-200" data-testid={`blog-card-${p.slug}`}>
        <div className="aspect-[16/10] overflow-hidden bg-ag-ink"><img src={p.cover} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
        <div className="p-6">
          <h3 className="font-display text-xl font-bold leading-tight text-ag-ink group-hover:text-ag-blue">{p.title}</h3>
          <p className="mt-2 line-clamp-2 text-sm text-slate-600">{p.excerpt}</p>
        </div>
      </Link>
    </Reveal>
  );
}
